import { apiRequest } from './client'
import type { AiTool, Paginated, ProjectDocId, TaskChecklistItem, TaskComment, TaskDetail, TaskHistoryEntry, TaskPriority, TaskSummary, TimeEntry } from './types'

export interface TaskListParams {
  projectId?: string
  statusId?: string
  assigneeUserId?: string
  assigneeRoleId?: string
  labelId?: string
  priority?: TaskPriority
  parentTaskId?: string
  topLevelOnly?: boolean
  mine?: boolean
  search?: string
  dueBefore?: string
  page?: number
  pageSize?: number
}

export const tasksApi = {
  list: (params: TaskListParams = {}) => apiRequest<Paginated<TaskSummary>>('/tasks', { query: params as Record<string, string | number | boolean | undefined> }),

  get: (id: string) => apiRequest<{ data: TaskDetail }>(`/tasks/${id}`),

  create: (input: {
    projectId: string
    title: string
    description?: string
    statusId?: string
    priority?: TaskPriority
    dueDate?: string
    startDate?: string
    estimatedMinutes?: number
    parentTaskId?: string
    assigneeUserIds?: string[]
    assigneeRoleIds?: string[]
    labelIds?: string[]
    documentIds?: string[]
    technicalScope?: string
    definitionOfDone?: string
    aiWorkInstruction?: string
    aiTool?: AiTool | null
    relevantProjectDocs?: ProjectDocId[]
  }) => apiRequest<{ data: TaskDetail }>('/tasks', { method: 'POST', body: input }),

  update: (
    id: string,
    input: Partial<{
      title: string
      description: string
      priority: TaskPriority
      dueDate: string | null
      startDate: string | null
      estimatedMinutes: number | null
      progress: number
      technicalScope: string
      definitionOfDone: string
      aiWorkInstruction: string
      aiTool: AiTool | null
      relevantProjectDocs: ProjectDocId[]
    }>,
  ) => apiRequest<{ data: TaskDetail }>(`/tasks/${id}`, { method: 'PATCH', body: input }),

  move: (id: string, statusId: string) => apiRequest<{ data: TaskDetail }>(`/tasks/${id}/status`, { method: 'PATCH', body: { statusId } }),

  setAssignees: (id: string, userIds: string[], roleIds: string[]) =>
    apiRequest<{ data: TaskDetail }>(`/tasks/${id}/assignees`, { method: 'PATCH', body: { userIds, roleIds } }),

  setLabels: (id: string, labelIds: string[]) => apiRequest<{ data: TaskDetail }>(`/tasks/${id}/labels`, { method: 'PATCH', body: { labelIds } }),

  setDocuments: (id: string, documentIds: string[]) =>
    apiRequest<{ data: TaskDetail }>(`/tasks/${id}/documents`, { method: 'PATCH', body: { documentIds } }),

  remove: (id: string) => apiRequest<{ data: { success: boolean } }>(`/tasks/${id}`, { method: 'DELETE' }),

  listComments: (id: string) => apiRequest<{ data: TaskComment[] }>(`/tasks/${id}/comments`),
  addComment: (id: string, body: string) => apiRequest<{ data: TaskComment }>(`/tasks/${id}/comments`, { method: 'POST', body: { body } }),
  updateComment: (id: string, commentId: string, body: string) =>
    apiRequest<{ data: TaskComment }>(`/tasks/${id}/comments/${commentId}`, { method: 'PATCH', body: { body } }),
  removeComment: (id: string, commentId: string) =>
    apiRequest<{ data: { success: boolean } }>(`/tasks/${id}/comments/${commentId}`, { method: 'DELETE' }),

  addChecklistItem: (id: string, text: string) => apiRequest<{ data: TaskChecklistItem }>(`/tasks/${id}/checklist`, { method: 'POST', body: { text } }),
  updateChecklistItem: (id: string, itemId: string, input: Partial<{ text: string; done: boolean }>) =>
    apiRequest<{ data: TaskChecklistItem }>(`/tasks/${id}/checklist/${itemId}`, { method: 'PATCH', body: input }),
  removeChecklistItem: (id: string, itemId: string) =>
    apiRequest<{ data: { success: boolean } }>(`/tasks/${id}/checklist/${itemId}`, { method: 'DELETE' }),
  reorderChecklist: (id: string, orderedIds: string[]) =>
    apiRequest<{ data: TaskChecklistItem[] }>(`/tasks/${id}/checklist/reorder`, { method: 'POST', body: { orderedIds } }),

  getHistory: (id: string) => apiRequest<{ data: TaskHistoryEntry[] }>(`/tasks/${id}/history`),

  listTimeEntries: (id: string) => apiRequest<{ data: TimeEntry[] }>(`/tasks/${id}/time`),
  getActiveTimer: () =>
    apiRequest<{ data: (TimeEntry & { task: { id: string; title: string; projectId: string } | null }) | null }>('/tasks/time/active'),
  startTimer: (id: string) => apiRequest<{ data: TimeEntry }>(`/tasks/${id}/time/start`, { method: 'POST', body: {} }),
  stopTimer: (id: string) => apiRequest<{ data: TimeEntry }>(`/tasks/${id}/time/stop`, { method: 'POST', body: {} }),
  addManualTime: (id: string, input: { startedAt: string; endedAt: string; comment?: string }) =>
    apiRequest<{ data: TimeEntry }>(`/tasks/${id}/time`, { method: 'POST', body: input }),
  removeTimeEntry: (id: string, entryId: string) =>
    apiRequest<{ data: { success: boolean } }>(`/tasks/${id}/time/${entryId}`, { method: 'DELETE' }),
}
